// v0.3.0: WebSocket client with auto-reconnect.
import type { ClientMsg, ServerMsg } from '../src/protocol';

export class Net {
  private ws: WebSocket | null = null;
  private closedByUs = false;
  private backoff = 500;
  private pingTimer: number | null = null;
  /** v1.21.2: heartbeat — when the last message came in, when the last ping went out, the measured round trip */
  private lastIn = 0;
  private pingAt = 0;
  rtt = 0;
  private retryTimer: number | null = null;
  /** where I'm standing, so a reconnect puts me back there (not at the entrance) */
  resumeAt: () => { x: number; y: number } | null = () => null;
  onMessage: (m: ServerMsg) => void = () => {};
  onStatus: (s: 'connecting' | 'open' | 'closed' | 'replaced' | 'removed') => void = () => {};

  connect(): void {
    if (this.retryTimer !== null) { clearTimeout(this.retryTimer); this.retryTimer = null; }
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return; // (one socket at a time)
    this.closedByUs = false;
    this.onStatus('connecting');
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const at = this.resumeAt();
    const ws = new WebSocket(`${proto}://${location.host}/ws${at ? `?x=${at.x}&y=${at.y}` : ''}`);
    this.ws = ws;
    ws.onopen = () => {
      this.backoff = 500;
      this.lastIn = Date.now();
      this.onStatus('open');
      // v1.21.2: ping every 5 s; a connection that has heard nothing for 12 s is dead (a silent Wi-Fi drop can keep a
      // socket "open" for minutes while moves go nowhere) -- drop it and reconnect at once
      this.pingTimer = window.setInterval(() => {
        if (this.ws !== ws) return;
        if (Date.now() - this.lastIn > 12_000) { console.warn('[net] no reply for 12 s, reconnecting'); this.reconnectNow(ws); return; }
        this.pingAt = performance.now();
        this.send({ t: 'ping' });
      }, 5000);
    };
    ws.onmessage = (ev) => {
      if (this.ws !== ws) return;
      this.lastIn = Date.now();
      let m: ServerMsg;
      try { m = JSON.parse(ev.data) as ServerMsg; } catch { return; }
      if (m.t === 'pong' && this.pingAt) { this.rtt = Math.round(performance.now() - this.pingAt); this.pingAt = 0; }
      this.onMessage(m);
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return; // (a socket we already gave up on)
      if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
      if (ev.code === 4001) { this.closedByUs = true; this.onStatus('replaced'); return; }
      if (ev.code === 4003) { this.closedByUs = true; this.onStatus('removed'); return; } // v1.19.0: removed from the workspace
      this.onStatus('closed');
      if (!this.closedByUs) {
        this.retryTimer = window.setTimeout(() => { this.retryTimer = null; this.connect(); }, this.backoff);
        this.backoff = Math.min(this.backoff * 2, 8000);
      }
    };
    ws.onerror = () => { /* onclose follows */ };
  }

  /** v1.21.2: abandon a dead socket and connect again right away */
  private reconnectNow(ws: WebSocket): void {
    if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
    this.ws = null;
    try { ws.close(4000, 'heartbeat timeout'); } catch { /* ignore */ }
    this.onStatus('closed');
    if (!this.closedByUs) this.connect();
  }

  /** v1.21.2: the network came back (or the tab woke up): if the socket isn't healthy, reconnect now, not after backoff */
  kick(): void {
    if (this.closedByUs) return;
    const ws = this.ws;
    if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) { this.ws = null; this.connect(); return; }
    if (ws.readyState === WebSocket.OPEN && Date.now() - this.lastIn > 8000) this.reconnectNow(ws);
  }

  send(m: ClientMsg): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m));
  }

  close(): void {
    this.closedByUs = true;
    this.ws?.close(1000, 'bye');
  }
}
